import { computed, ref, watch } from 'vue';
import {
  extractLearnHeadings,
  learnChapters,
  learnChapterHeading,
  learnChapterTitle,
  slugifyHeading,
} from '@/lib/learn-docs';
import { i18n } from '@/i18n';

interface SearchSection {
  slug: string;
  title: string;
  heading: string;
  headingId: string;
  text: string;
  textLower: string;
  titleLower: string;
  headingLower: string;
  line: number;
}

export interface LearnSearchHit {
  slug: string;
  title: string;
  heading: string;
  headingId: string;
  snippet: string;
  score: number;
  line: number;
}

const STRIP_HTML = /<[^>]+>/g;
const STRIP_MD = /^#{1,6}\s+|[*_`~\x5b\x5d()!|>]/g;
const STRIP_FENCE = /^```[\s\S]*?^```/gm;

const query = ref('');
const loading = ref(false);
const results = ref<LearnSearchHit[]>([]);
const index = ref<SearchSection[] | null>(null);

watch(
  () => i18n.locale,
  () => {
    index.value = null;
  }
);

function normalize(text: string) {
  return text
    .replace(STRIP_HTML, ' ')
    .replace(STRIP_FENCE, ' ')
    .replace(STRIP_MD, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text: string) {
  return text
    .toLowerCase()
    .split(/[\s,.;:!?/]+/)
    .filter((token) => token.length >= 2);
}

function buildSections(slug: string, title: string, heading: string, raw: string) {
  const sections: SearchSection[] = [];
  const lines = raw.split(/\r?\n/);
  const headings = extractLearnHeadings(raw);
  let currentHeading = heading;
  let currentHeadingId = '';
  let buffer: string[] = [];
  let startLine = 0;

  const flush = () => {
    if (buffer.length === 0) return;
    const text = normalize(buffer.join(' '));
    if (!text) {
      buffer = [];
      return;
    }

    sections.push({
      slug,
      title,
      heading: currentHeading,
      headingId: currentHeadingId,
      text,
      textLower: text.toLowerCase(),
      titleLower: title.toLowerCase(),
      headingLower: currentHeading.toLowerCase(),
      line: startLine,
    });

    buffer = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const headingMatch = line.match(/^##\s+(.+)$/);

    if (headingMatch) {
      flush();
      currentHeading = headingMatch[1]
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .trim();
      currentHeadingId =
        headings.find((item) => item.text === currentHeading)?.id ?? slugifyHeading(currentHeading);
      startLine = index;
      continue;
    }

    if (buffer.length === 0) {
      startLine = index;
    }
    buffer.push(line);
  }

  flush();

  return sections;
}

async function ensureIndex() {
  if (index.value) return index.value;

  loading.value = true;

  try {
    const loaded = await Promise.all(
      learnChapters
        .filter((chapter) => !chapter.hidden)
        .map(async (chapter) => {
          const mod = await chapter.load();
          return buildSections(
            chapter.slug,
            learnChapterTitle(chapter.slug),
            learnChapterHeading(chapter.slug),
            mod.default
          );
        })
    );

    index.value = loaded.flat();
    return index.value;
  } finally {
    loading.value = false;
  }
}

function scoreSection(section: SearchSection, rawQuery: string, tokens: string[]) {
  const queryLower = rawQuery.toLowerCase();
  let score = 0;

  if (section.titleLower.includes(queryLower)) score += 120;
  if (section.headingLower.includes(queryLower)) score += 90;
  if (section.textLower.includes(queryLower)) score += 25;

  let matchedTokens = 0;

  for (const token of tokens) {
    if (section.headingLower.includes(token)) {
      score += 30;
      matchedTokens += 1;
      continue;
    }

    if (section.textLower.includes(token)) {
      score += 10;
      matchedTokens += 1;
    }
  }

  if (tokens.length > 1 && matchedTokens === tokens.length) {
    score += 20;
  }

  if (score > 0 && section.text.length < 220) {
    score += 5;
  }

  return score;
}

function extractSnippet(text: string, tokens: string[]) {
  const fallback = text.slice(0, 160);
  if (tokens.length === 0) return fallback;

  const lower = text.toLowerCase();
  let bestIndex = -1;

  for (const token of tokens) {
    const index = lower.indexOf(token.toLowerCase());
    if (index !== -1 && (bestIndex === -1 || index < bestIndex)) {
      bestIndex = index;
    }
  }

  if (bestIndex === -1) return fallback;

  const start = Math.max(0, bestIndex - 60);
  const end = Math.min(text.length, bestIndex + 100);
  const snippet = text.slice(start, end).trim();

  if (start > 0 && end < text.length) return `...${snippet}...`;
  if (start > 0) return `...${snippet}`;
  if (end < text.length) return `${snippet}...`;
  return snippet;
}

async function runSearch() {
  const trimmed = query.value.trim();
  if (trimmed.length < 2) {
    results.value = [];
    return;
  }

  const sections = await ensureIndex();
  const tokens = tokenize(trimmed);

  results.value = sections
    .map((section) => {
      const score = scoreSection(section, trimmed, tokens);
      return score > 0
        ? {
            slug: section.slug,
            title: section.title,
            heading: section.heading,
            headingId: section.headingId,
            snippet: extractSnippet(section.text, tokens),
            score,
            line: section.line,
          }
        : null;
    })
    .filter((item): item is LearnSearchHit => Boolean(item))
    .sort((left, right) => right.score - left.score)
    .slice(0, 10);
}

watch(query, () => {
  void runSearch();
});

export function useLearnDocsSearch() {
  const hasQuery = computed(() => query.value.trim().length >= 2);

  function resetSearch() {
    query.value = '';
    results.value = [];
  }

  return {
    query,
    results,
    loading,
    hasQuery,
    resetSearch,
  };
}
